import {useState} from 'react'
import {useI18n} from '@/i18n'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import {SqlEditor} from '@/components/editors/SqlEditor'
import {TableHead, TableHeader, TableRow, Table, TableBody, TableCell} from '@/components/ui/table'
import {formatTimestamp} from '@/lib/date-utils'
import type {Span} from '@/types/observability'
import {SpanKind, SpanStatus} from '@/types/observability'
import {BadgeVariant} from '@/components/ui/badge'

const MAX_DETAIL_CHARS = 80
type SortMode = 'time' | 'duration'

interface SpanWaterfallProps {
    spans: Span[]
    onSpanClick: (span: Span) => void
    selectedSpanId?: string | null
}

/* eslint-disable i18next/no-literal-string -- CSS class names, not user-visible text */
function kindColor(kind: Span['kind']): string {
    switch (kind) {
        case SpanKind.HTTP_REQUEST: return 'bg-blue-500'
        case SpanKind.CONTROLLER: return 'bg-indigo-400'
        case SpanKind.SERVICE: return 'bg-purple-400'
        case SpanKind.SQL: return 'bg-amber-400'
        case SpanKind.EXTERNAL_HTTP: return 'bg-cyan-400'
        case SpanKind.FRONTEND_RENDER: return 'bg-green-400'
        case SpanKind.FRONTEND_ACTION: return 'bg-emerald-400'
        default: return 'bg-muted-foreground'
    }
}
/* eslint-enable i18next/no-literal-string */

export function spanTooltip(span: Span): string {
    if (span.kind === SpanKind.SQL && span.sqlStatement) {
        return span.sqlStatement
    }
    if ((span.kind === SpanKind.HTTP_REQUEST || span.kind === SpanKind.EXTERNAL_HTTP) && span.httpUrl) {
        return `${span.httpMethod ?? ''} ${span.httpUrl} → ${span.httpStatusCode ?? ''}`
    }
    return span.operationName
}

function spanDetail(span: Span): string | null {
    if (span.kind === SpanKind.SQL && span.sqlStatement) {
        const stmt = span.sqlStatement.length > MAX_DETAIL_CHARS
            ? span.sqlStatement.substring(0, MAX_DETAIL_CHARS) + '…'
            : span.sqlStatement
        return stmt
    }
    if ((span.kind === SpanKind.HTTP_REQUEST || span.kind === SpanKind.EXTERNAL_HTTP) && span.httpUrl) {
        return `${span.httpMethod ?? ''} ${span.httpUrl}`
    }
    return null
}

function buildDepthMap(spans: Span[]): Map<string, number> {
    const depths = new Map<string, number>()
    const spanById = new Map<string, Span>()
    for (const span of spans) {
        spanById.set(span.spanId, span)
    }
    function getDepth(spanId: string): number {
        if (depths.has(spanId)) return depths.get(spanId)!
        const span = spanById.get(spanId)
        if (!span || !span.parentSpanId) {
            depths.set(spanId, 0)
            return 0
        }
        const parentDepth = getDepth(span.parentSpanId)
        depths.set(spanId, parentDepth + 1)
        return parentDepth + 1
    }
    for (const span of spans) {
        getDepth(span.spanId)
    }
    return depths
}

export function SpanWaterfall({spans, onSpanClick, selectedSpanId}: SpanWaterfallProps) {
    const i18n = useI18n()
    const [sortMode, setSortMode] = useState<SortMode>('time')
    const [kindFilter, setKindFilter] = useState<string | null>(null)

    if (spans.length === 0) return null

    const filtered = kindFilter ? spans.filter(s => s.kind === kindFilter) : spans
    const sorted = [...filtered].sort((a, b) =>
        sortMode === 'duration' ? b.durationMs - a.durationMs : a.startedAt.localeCompare(b.startedAt)
    )
    const depths = buildDepthMap(sorted)
    const maxSpanDurationMs = Math.max(...sorted.map(s => s.durationMs), 1)

    const kindCounts = new Map<string, number>()
    for (const s of spans) {
        kindCounts.set(s.kind, (kindCounts.get(s.kind) ?? 0) + 1)
    }

    return (
        <div data-testid="span-waterfall">
            { }
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Button
                    variant={sortMode === 'time' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSortMode('time')}
                    data-testid="sort-time"
                >
                    {i18n.requestInspector.waterfall.sortByTime}
                </Button>
                <Button
                    variant={sortMode === 'duration' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSortMode('duration')}
                    data-testid="sort-duration"
                >
                    {i18n.requestInspector.waterfall.sortByDuration}
                </Button>
                <span className="mx-2 text-muted-foreground">|</span>
                <Button
                    variant={kindFilter === null ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setKindFilter(null)}
                    data-testid="filter-all"
                >
                    {i18n.requestInspector.waterfall.filterAll} ({spans.length})
                </Button>
                {Array.from(kindCounts.entries()).map(([kind, count]) => (
                    <Button
                        key={kind}
                        variant={kindFilter === kind ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setKindFilter(kindFilter === kind ? null : kind)}
                        data-testid={`filter-${kind}`}
                    >
                        {kind} ({count})
                    </Button>
                ))}
            </div>
            { }
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[40%]" data-testid="col-span">
                            {i18n.requestInspector.waterfall.colSpan}
                        </TableHead>
                        <TableHead className="w-[40%]" data-testid="col-duration">
                            {i18n.requestInspector.waterfall.colDuration}
                        </TableHead>
                        <TableHead className="w-[20%]" data-testid="col-status">
                            {i18n.requestInspector.waterfall.colStatus}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {sorted.map((span) => {
                        const depth = depths.get(span.spanId) ?? 0
                        const widthPct = Math.max((span.durationMs / maxSpanDurationMs) * 100, 0.5)
                        const isSelected = span.spanId === selectedSpanId
                        const isError = span.status === SpanStatus.ERROR

                        return (
                            <TableRow
                                key={span.spanId}
                                className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-muted' : ''} ${isError ? 'text-destructive' : ''}`}
                                onClick={() => onSpanClick(span)}
                                data-testid={`waterfall-row-${span.spanId}`}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        onSpanClick(span)
                                    }
                                }}
                            >
                                <TableCell>
                                    <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div style={{paddingLeft: `${depth * 16}px`}} className="min-w-0 cursor-pointer">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${kindColor(span.kind)}`}/>
                                                        <span className="font-mono text-xs truncate">
                                                            {span.operationName}
                                                        </span>
                                                    </div>
                                                    {spanDetail(span) && (
                                                        <div className="font-mono text-[10px] text-muted-foreground truncate ml-4 mt-0.5" data-testid={`span-detail-${span.spanId}`}>
                                                            {spanDetail(span)}
                                                        </div>
                                                    )}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" align="start" className="max-w-xl bg-popover text-popover-foreground border shadow-lg p-0">
                                                {span.kind === SpanKind.SQL && span.sqlStatement ? (
                                                    /* eslint-disable-next-line i18next/no-literal-string -- CSS dimensions */
                                                    <SqlEditor sql={span.sqlStatement} onChange={() => {}} readOnly minHeight="40px" maxHeight="200px"/>
                                                ) : (
                                                    <pre className="font-mono text-xs whitespace-pre-wrap break-all p-3">{spanTooltip(span)}</pre>
                                                )}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </TableCell>
                                <TableCell>
                                    <div className="relative h-5 bg-muted rounded overflow-hidden" title={formatTimestamp(span.startedAt)}>
                                        <div
                                            className={`absolute top-0 h-full rounded opacity-80 ${kindColor(span.kind)}`}
                                            style={{left: 0, width: `${widthPct}%`}}
                                        />
                                        <span className="absolute inset-0 flex items-center px-1 text-[10px] font-mono z-10 pointer-events-none">
                                            {/* eslint-disable-next-line i18next/no-literal-string -- unit suffix, not user-visible text */}
                                            {span.durationMs}ms
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={span.status === SpanStatus.ERROR ? BadgeVariant.DESTRUCTIVE : span.status === SpanStatus.CANCELLED ? BadgeVariant.WARNING : BadgeVariant.SUCCESS}
                                        className="text-[10px]"
                                    >
                                        {span.status}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </div>
    )
}
